import { FC, useState, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Grid, Row, Col } from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import { RsInput, RsInputSize } from 'components/Rsuite/Input';
import { RsButton, RsButtonSize } from 'components/Rsuite/Button';
import {
  RsModalTrigger,
  RsConfirmationModalTrigger,
} from 'components/Rsuite/Modal';

import { getComplement, getUnion, getIntersection } from 'utils/SetUtils';

import {
  ManualKeywordProperty,
  MANUAL_UNIQUE_KEY,
  ManualKeywordType,
  SuggestKeywordProperty,
  SuggestKeywordType,
  SUGGEST_KEYWORD_UNIQUE_KEY,
} from 'core/rtbCampaign/singlePage/keyword';
import { BidBatchEdit } from './BidBatchEdit';
import { ChosenKeywordTable } from './ChosenKeywordTable';
import { ManualKeywordEditBoard } from './ManualKeywordEditBoard';
import { searchMatch } from './utils';

import styles from './manualKeyword.module.scss';

type KeyType = ManualKeywordType[typeof MANUAL_UNIQUE_KEY];

interface ManualKeywordProps {
  data: ManualKeywordType[];
  onChange: (data: ManualKeywordType[]) => void;
  suggestKeywords: SuggestKeywordType[];
  keywordErrorsMap?: Record<string, any>;
}

export const ManualKeyword: FC<ManualKeywordProps> = ({
  data = [],
  onChange,
  suggestKeywords,
  keywordErrorsMap = {},
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [checkedKeys, setCheckedKeys] = useState(() => new Set<KeyType>());

  const filteredData = useMemo(
    () => data.filter(({ keyword }) => searchMatch(searchValue, keyword)),
    [data, searchValue],
  );

  const suggestBidMap = useMemo(
    () =>
      suggestKeywords.reduce(
        (map, item) =>
          map.set(
            item[SUGGEST_KEYWORD_UNIQUE_KEY],
            Number(item[SuggestKeywordProperty.CPC]),
          ),
        new Map(),
      ),
    [suggestKeywords],
  );

  const filterDataWithSuggestBid = useMemo(
    () =>
      filteredData.map((item) => ({
        ...item,
        suggestPrice: suggestBidMap.get(item[ManualKeywordProperty.KEYWORD]),
      })),
    [filteredData, suggestBidMap],
  );

  const filteredDataKeys = useMemo(
    () => new Set(filteredData.map((item) => item[MANUAL_UNIQUE_KEY])),
    [filteredData],
  );

  const filteredCheckedKeys = useMemo(
    () => getIntersection(checkedKeys, filteredDataKeys),
    [checkedKeys, filteredDataKeys],
  );

  const handleBidPrice = useCallback(
    (uniqueKey, value) => {
      const newData = data.map((item) =>
        item[MANUAL_UNIQUE_KEY] === uniqueKey
          ? {
              ...item,
              [ManualKeywordProperty.PRICE]: value,
            }
          : item,
      );
      onChange(newData);
    },
    [data, onChange],
  );

  const handleDelete = useCallback(
    (uniqueKey) => {
      const newData = data.filter(
        (item) => item[MANUAL_UNIQUE_KEY] !== uniqueKey,
      );
      onChange(newData);
      setCheckedKeys(getComplement(checkedKeys, new Set([uniqueKey])));
    },
    [data, checkedKeys, onChange],
  );

  const handleBatchDelete = useCallback(() => {
    const newData = data.filter(
      ({ [MANUAL_UNIQUE_KEY]: uniqueKey }) =>
        !filteredCheckedKeys.has(uniqueKey),
    );
    onChange(newData);
    setCheckedKeys(getComplement(checkedKeys, filteredCheckedKeys));
  }, [data, checkedKeys, filteredCheckedKeys, onChange]);

  const handleSelect = useCallback(
    (uniqueKey) => {
      const newCheckedKeys = checkedKeys.has(uniqueKey)
        ? getComplement(checkedKeys, new Set([uniqueKey]))
        : getUnion(checkedKeys, new Set([uniqueKey]));
      setCheckedKeys(newCheckedKeys);
    },
    [checkedKeys],
  );

  const handleAllSelect = useCallback(() => {
    const newCheckedKeys =
      filteredCheckedKeys.size === filteredDataKeys.size
        ? getComplement(checkedKeys, filteredCheckedKeys)
        : getUnion(checkedKeys, filteredDataKeys);
    setCheckedKeys(newCheckedKeys);
  }, [checkedKeys, filteredCheckedKeys, filteredDataKeys]);

  return (
    <div className={styles.ManualKeyword}>
      <Grid fluid>
        <Row className='mb-[12px]'>
          <Col xs={12} className='d-flex items-center gap-[12px]'>
            <p
              className={`font-bold ${data.length > 1000 ? 'text-red-500' : ''}`}
            >
              已選關鍵字：{data.length} / 1000
            </p>
            <RsInput
              value={searchValue}
              onChange={setSearchValue}
              size={RsInputSize.SMALL}
              frontAddon={<SearchIcon />}
              placeholder='篩選已選關鍵字'
            />
          </Col>
          <Col xs={12} className='d-flex justify-content-end gap-[12px]'>
            <RsModalTrigger
              modalProps={{ size: 'sm' }}
              renderModalTrigger={(openModal) => (
                <RsButton
                  appearance='primary'
                  onClick={openModal}
                  disabled={!filteredCheckedKeys.size}
                >
                  批量編輯
                </RsButton>
              )}
              renderModalContent={(closeModal) => (
                <BidBatchEdit
                  data={data}
                  effectiveKeys={filteredCheckedKeys}
                  suggestBidMap={suggestBidMap}
                  onChange={(newData) => {
                    onChange(newData);
                    closeModal();
                    toast.success('成功批量編輯關鍵字');
                  }}
                  onCancel={closeModal}
                />
              )}
            />
            <RsConfirmationModalTrigger
              renderModalTrigger={(openModal) => (
                <RsButton
                  appearance='ghost'
                  onClick={openModal}
                  disabled={!filteredCheckedKeys.size}
                >
                  批量刪除
                </RsButton>
              )}
              message={
                <span className='text-[16px]'>確認將所選關鍵字刪除?</span>
              }
              onConfirm={(closeModal) => {
                handleBatchDelete();
                closeModal();
              }}
            />
          </Col>
        </Row>
        <Row>
          <ChosenKeywordTable
            data={filterDataWithSuggestBid}
            onBidChange={handleBidPrice}
            onDelete={handleDelete}
            checkedKeys={filteredCheckedKeys}
            onSelect={handleSelect}
            onAllSelect={handleAllSelect}
            keywordErrorsMap={keywordErrorsMap}
          />
        </Row>
        <Row className='d-flex h-[48px] items-center bg-white px-[24px] shadow-sm'>
          <RsModalTrigger
            renderModalTrigger={(openModal) => (
              <RsButton
                appearance='link'
                size={RsButtonSize.LARGE}
                onClick={openModal}
              >
                + 新增關鍵字
              </RsButton>
            )}
            renderModalContent={(closeModal) => (
              <ManualKeywordEditBoard
                chosen={data}
                onChosenChange={(newData) => {
                  onChange(newData);
                  closeModal();
                }}
                suggestKeywords={suggestKeywords}
                onCancel={closeModal}
              />
            )}
          />
        </Row>
      </Grid>
    </div>
  );
};
